import isEmpty from 'lodash/isEmpty';
import isBoolean from 'lodash/isBoolean';
import keyBy from 'lodash/fp/keyBy';
import flow from 'lodash/flow';
import {
  fetchTours,
  fetchToursSuccess,
  fetchToursFailure,
  addToTourQueue,
  removeFromTourQueue,
  prioritizeTour,
  incrementTourStep,
  decrementTourStep,
  putTourSettingsSuccess,
  putTourSettingsFailure,
  completeCurrentTour
} from './actions';
import { filterViewedSteps } from './util';
import { ThunkAction, Dispatch, GetState } from './types.js';
import { FtueTour } from 'types';
import { putTourStep, getTourSteps } from 'api';
import { removeMultipleTourStepByTourName, removeAllTourStepsByTourName } from 'state/modules/user';

const getSingleStepViewed = (steps, currentStepIndex) => {
  const step = steps[currentStepIndex];

  if (step.steps && Array.isArray(step.steps)) {
    return step.steps.map(x => ({ name: x.name, viewed: true }));
  }

  return [
    {
      name: step.name,
      viewed: true
    }
  ];
};

const getAllStepsViewed = steps => {
  return steps
    .map(x => {
      if (x.steps && Array.isArray(x.steps)) {
        return x.steps.map(y => ({ name: y.name, viewed: true }));
      }

      return { name: x.name, viewed: true };
    })
    .flat();
};

export function getTours() {
  return async (dispatch: Dispatch, getState) => {
    const { toursViewed } = getState().tour;
    dispatch(fetchTours());

    try {
      const response = await getTourSteps();
      const tours = flow(filterViewedSteps(toursViewed), keyBy('name'))(response.data);

      return dispatch(fetchToursSuccess(tours as any));
    } catch (err) {
      dispatch(fetchToursFailure(err as any));
    }
  };
}

export function saveTourStep(tour: FtueTour[]) {
  return async (dispatch: Dispatch) => {
    try {
      await putTourStep(tour);
      dispatch(putTourSettingsSuccess);
    } catch (err) {
      dispatch(putTourSettingsFailure);
    }
  };
}

export function advanceTourStep(): ThunkAction {
  return async (dispatch: Dispatch, getState: GetState) => {
    const { allTours, queuedTours, currentStep: currentStepIndex } = getState().tour;
    const currentTour = allTours[queuedTours[0]];
    const viewedSteps = getSingleStepViewed(currentTour.steps, currentStepIndex);
    const updatedTour = { name: currentTour.name, steps: viewedSteps };

    dispatch(saveTourStep([updatedTour]));
    await dispatch(incrementTourStep(viewedSteps));

    dispatch(
      removeMultipleTourStepByTourName({
        tourName: currentTour.name,
        stepNames: viewedSteps.map(x => x.name)
      }) as any
    );

    if (currentTour.steps.length === currentStepIndex + 1) {
      dispatch(completeCurrentTour());
    }
  };
}

export function completeAllSteps(): ThunkAction {
  return async (dispatch: Dispatch, getState: GetState) => {
    const { allTours, queuedTours } = getState().tour;
    const currentTour = allTours[queuedTours[0]];
    const viewedSteps = getAllStepsViewed(currentTour.steps);
    const updatedTour = { name: currentTour.name, steps: viewedSteps };

    // complete tour
    dispatch(saveTourStep([updatedTour]));
    dispatch(completeCurrentTour());
    dispatch(
      removeAllTourStepsByTourName({
        tourName: currentTour.name
      }) as any
    );
  };
}

export function goBackTourStep(): ThunkAction {
  return async (dispatch: Dispatch) => {
    await dispatch(decrementTourStep() as any);
  };
}

export function queueTour(name: string, prioritize?: boolean): ThunkAction {
  return async (dispatch: Dispatch, getState) => {
    const {
      tour: { queuedTours }
    }: any = getState();
    const notQueued = !queuedTours.includes(name);
    // const hasUnviewedSteps = processHasUnviewedSteps(name, ftue);
    const enqueue = isBoolean(prioritize) && prioritize ? prioritizeTour : addToTourQueue;

    if (/*hasUnviewedSteps &&*/ notQueued) {
      dispatch(enqueue(name));
    }
  };
}

export function dequeueTour(...names: string[]) {
  return async (dispatch: Dispatch, getState) => {
    const { queuedTours } = getState().tour;

    if (isEmpty(queuedTours)) return;

    dispatch(removeFromTourQueue(names));
  };
}
